import type { AutoVJLook, AutoVJLookLayer } from './autoVJ';
import type { EffectLayout } from './effectLayout';
import type { EffectStackLayer } from './effectStack';
import { effectRoomPathParams, type SchematicLayout } from './panelElements';
import { sendSettingAwaited, type SettingHold, type SettingWrite } from '../state/effectSettings';
import type { VenueContextValue } from '../state/VenueProvider';

/**
 * PLAY A LOOK: put a composed look on the room and hold it there until the ordinary Stop.
 *
 * Owner, 2026-09-23: Play on anything that holds a look sends that look to the room and holds it until
 * Stop — not a unit's dwell or transition — with no prompt, because effects replace effects (B62). It is
 * exactly what the Audio reactive card does when a saved look is recalled and started: ONE settings
 * message carrying the look's layers and mix, then the card's own
 * `start_audio_reactive`. No new command exists for it; the room's audio effect renders its stack and
 * keeps rendering it until someone stops it.
 */

type StackReplaceLayer = Omit<EffectStackLayer, 'id'>;

/** A stack layer as a look or the stack editor holds it: no daemon id, or one the daemon will replace. */
type LookLayer = AutoVJLookLayer;

/**
 * One layer at the strict daemon boundary.
 *
 * A stack read carries `id`; a whole-stack replace deliberately does not. The daemon assigns every new id
 * and decodes this operation with unknown fields forbidden, so spreading a saved or read layer here would
 * turn a harmless local identity into a refusal of the entire look. The wire shape is built field by
 * field so neither `id` nor a future console-only property can cross this boundary.
 */
export function stackLayerForWire(layer: LookLayer): StackReplaceLayer {
  const driver = layer.driver;
  return {
    effectId: layer.effectId,
    zone: layer.zone,
    parameters: { ...(layer.parameters ?? {}) },
    sync: { enabled: layer.sync.enabled, musicRate: layer.sync.musicRate },
    ...(driver ? {
      driver: {
        source: driver.source as NonNullable<EffectStackLayer['driver']>['source'],
        threshold: driver.threshold,
        amount: driver.amount,
        attack: driver.attack,
        release: driver.release,
        target: driver.target,
      },
    } : {}),
  };
}

/** The whole-stack replace that puts a look on the room. */
export function lookStackReplace(look: AutoVJLook): SettingWrite[] {
  return [
    { path: 'mix', value: look.mix === 'add' ? 'add' : 'max' },
    { path: 'layers', value: (look.layers ?? []).map(stackLayerForWire) },
  ];
}

/** The room's chosen music input, as the Audio reactive card and Play both hand it to the controller. */
export interface AudioStartInput {
  kind?: string;
  identity: string;
  signal?: unknown;
}

/** `start_audio_reactive`'s parameters: the room's layout, its chosen input and its authored walk. */
export function audioReactiveStartParams(
  layout: EffectLayout,
  input: AudioStartInput,
  roomPath: SchematicLayout | undefined,
): Record<string, unknown> {
  return {
    layout,
    input: input.kind === 'synthetic'
      ? { kind: 'synthetic', signal: String(input.signal ?? '') }
      : { kind: 'device', identity: input.identity },
    ...effectRoomPathParams(roomPath),
  };
}

export interface PlayLookRoom {
  venue: VenueContextValue;
  call: (name: string, params: Record<string, unknown>) => Promise<unknown>;
  hold?: SettingHold;
  layout: EffectLayout;
  input: AudioStartInput;
  roomPath?: SchematicLayout;
  /** The room already runs its audio effect, which renders the replaced stack with no second start. */
  audioRunning: boolean;
}

/** A look with nothing in it would blank the room; Play is offered only for a look that paints. */
export function lookPlayable(look: AutoVJLook): boolean {
  return (look.layers ?? []).length > 0;
}

export async function playLook(room: PlayLookRoom, look: AutoVJLook): Promise<void> {
  room.hold?.clearReplacedLayers();
  await sendSettingAwaited(room.call, 'nuance.audio-reactive', lookStackReplace(look), room.hold, room.venue);
  if (room.audioRunning) return;
  await room.call('start_audio_reactive', audioReactiveStartParams(room.layout, room.input, room.roomPath));
}
